// src/utils/coloredText.js
//
// Coloured measurement notes — the shared segment model.
//
// DATA CONTRACT (source of truth — the fitter portal repo duplicates this
// file's logic; keep the shape stable across both apps):
//
//   room.comments          : string  — the plain note, exactly as before.
//   room.commentsSegments  : Array<{ text: string, color: 'red'|'green'|'orange'|null }>
//
// `comments` is ALWAYS the concatenation of every segment's `.text`. It is a
// derived join, never edited independently, so the plain string and the
// coloured view can never drift. A room with no `commentsSegments` (every room
// that predates this feature) means "no colouring — render `comments` plain",
// exactly like before.
//
// Everything in here is pure (no JSX, no React) so it can be unit-reasoned and
// reused from both the editor and the read-only renderers. The React render
// lives in src/components/common/ColoredComments.jsx.
//
// ⚠️ Tailwind JIT: the class strings below are written as LITERALS. Do not
// rewrite them as `text-${color}-600` — the JIT scanner only sees literal class
// names in source and would purge them from the bundle. Same lesson as
// src/utils/eventTypeStyles.js.

/** The only colours a segment may carry. Fixed enum, per Yamen's ruling. */
export const COLOR_CLASSES = {
  red: "text-red-600",
  green: "text-green-600",
  orange: "text-orange-600",
};

/** Editor toolbar options, in display order. `value: null` = clear colour. */
export const COLOR_CHOICES = [
  { value: "red", label: "Red", swatch: "bg-red-600", ring: "ring-red-300" },
  { value: "green", label: "Green", swatch: "bg-green-600", ring: "ring-green-300" },
  { value: "orange", label: "Orange", swatch: "bg-orange-600", ring: "ring-orange-300" },
];

/**
 * Coerce anything to a legal colour value.
 * Unknown/legacy/garbage colours degrade to plain rather than throwing.
 */
export const normalizeColor = (color) =>
  Object.prototype.hasOwnProperty.call(COLOR_CLASSES, color) ? color : null;

/** Tailwind class for a segment colour ("" for plain text). */
export const colorClassName = (color) => COLOR_CLASSES[normalizeColor(color)] || "";

/** The plain string a segment array represents. */
export const segmentsToText = (segments) =>
  (Array.isArray(segments) ? segments : [])
    .map((seg) => (typeof seg?.text === "string" ? seg.text : ""))
    .join("");

/** Total plain-character length of a segment array. */
export const segmentsLength = (segments) => segmentsToText(segments).length;

/** Wrap a plain string as a single uncoloured segment (empty string → []). */
export const textToSegments = (text) =>
  text ? [{ text: String(text), color: null }] : [];

/**
 * Merge adjacent same-colour runs and drop empty ones.
 * Keeps stored segments minimal so Firestore docs stay small and diffs readable.
 */
export const normalizeSegments = (segments) => {
  const out = [];

  for (const seg of Array.isArray(segments) ? segments : []) {
    const text = typeof seg?.text === "string" ? seg.text : "";
    if (!text) continue;

    const color = normalizeColor(seg?.color);
    const last = out[out.length - 1];

    if (last && last.color === color) {
      last.text += text;
    } else {
      out.push({ text, color });
    }
  }

  return out;
};

/**
 * Is this segment array a faithful decoration of `text`?
 *
 * Every reader MUST gate on this before rendering colours. If the two ever
 * disagree (a legacy write, a hand-edited doc, a future writer that forgot to
 * keep them in step), the plain `comments` string wins and colouring is
 * skipped — the note is never shown wrong or shown twice.
 */
export const isValidSegments = (segments, text) =>
  Array.isArray(segments) &&
  segments.length > 0 &&
  segmentsToText(segments) === (text || "");

/**
 * Slice a segment array by plain-character range [from, to), preserving colours.
 * Segments straddling a boundary are split.
 */
export const sliceSegments = (segments, from, to) => {
  const out = [];
  let pos = 0;

  for (const seg of Array.isArray(segments) ? segments : []) {
    const text = typeof seg?.text === "string" ? seg.text : "";
    const segStart = pos;
    const segEnd = pos + text.length;
    pos = segEnd;

    if (segEnd <= from || segStart >= to) continue;

    const piece = text.slice(
      Math.max(from, segStart) - segStart,
      Math.min(to, segEnd) - segStart
    );

    if (piece) out.push({ text: piece, color: normalizeColor(seg?.color) });
  }

  return out;
};

/** Colour of the character at `index`, or null (also for out-of-range). */
export const colorAt = (segments, index) => {
  if (index < 0) return null;

  let pos = 0;
  for (const seg of Array.isArray(segments) ? segments : []) {
    const text = typeof seg?.text === "string" ? seg.text : "";
    if (index < pos + text.length) return normalizeColor(seg?.color);
    pos += text.length;
  }

  return null;
};

/**
 * Paint [start, end) with `color` (null clears). Everything outside is untouched.
 * This is what the Red / Green / Orange / Clear buttons call.
 */
export const applyColorToRange = (segments, start, end, color) => {
  const length = segmentsLength(segments);
  const from = Math.max(0, Math.min(start, length));
  const to = Math.max(from, Math.min(end, length));

  if (from === to) return normalizeSegments(segments);

  return normalizeSegments([
    ...sliceSegments(segments, 0, from),
    { text: segmentsToText(sliceSegments(segments, from, to)), color: normalizeColor(color) },
    ...sliceSegments(segments, to, length),
  ]);
};

/**
 * Replace plain-character range [start, end) with `insertText`.
 *
 * Colour inheritance follows the word-processor convention, which is what
 * staff will expect:
 *   - Replacing a selection (from < to) takes the colour of the FIRST
 *     character being replaced — select a red word, type over it, still red.
 *   - A pure insertion (from === to) takes the colour of the character
 *     immediately BEFORE the caret — typing inside a red run continues red;
 *     typing at position 0 is plain.
 */
export const replaceRange = (segments, start, end, insertText) => {
  const length = segmentsLength(segments);
  const from = Math.max(0, Math.min(start, length));
  const to = Math.max(from, Math.min(end, length));

  const inheritedColor =
    to > from ? colorAt(segments, from) : colorAt(segments, from - 1);

  const inserted = insertText
    ? [{ text: String(insertText), color: inheritedColor }]
    : [];

  return normalizeSegments([
    ...sliceSegments(segments, 0, from),
    ...inserted,
    ...sliceSegments(segments, to, length),
  ]);
};

/**
 * Fold a plain-text edit (whatever the textarea just did — type, delete, cut,
 * drag, autocorrect) back into the segment array.
 *
 * We only get the before/after strings from the DOM, so we recover the edit as
 * "the span between the common prefix and the common suffix was replaced".
 * That is exact for every single-range edit, which is all a textarea can
 * produce.
 */
export const applyTextEdit = (segments, oldText, newText) => {
  const before = oldText || "";
  const after = newText || "";

  const shortest = Math.min(before.length, after.length);

  let prefix = 0;
  while (prefix < shortest && before[prefix] === after[prefix]) prefix += 1;

  let suffix = 0;
  while (
    suffix < shortest - prefix &&
    before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  ) {
    suffix += 1;
  }

  return replaceRange(
    segments,
    prefix,
    before.length - suffix,
    after.slice(prefix, after.length - suffix)
  );
};

/**
 * Prepare a segment array for storage.
 *
 * If nothing is actually coloured we store `[]`, so a note that was never
 * decorated looks identical on disk to one written before this feature existed.
 * Keeps old and new rooms indistinguishable for every plain reader (invoices).
 */
export const segmentsForStorage = (segments) => {
  const normalized = normalizeSegments(segments);
  return normalized.some((seg) => seg.color) ? normalized : [];
};
